import type { IconName } from '../components/icons';

/**
 * Der LinkedIn-Prozess als geordnete Schrittliste. `requires` sind harte
 * Voraussetzungen (ohne sie ist der Schritt gesperrt), `recommends` weiche
 * (der Schritt geht, aber die KI hat weniger Kontext).
 */
export type FlowStepId =
  | 'strategy' | 'profiles' | 'series' | 'ideas' | 'briefs' | 'post' | 'plan' | 'calendar' | 'measure';

/** Fuenf Phasen fassen die neun Schritte zusammen (Seitenleiste, Schrittleiste, Uebersicht). */
export type FlowPhaseId = 'foundation' | 'formats' | 'posts' | 'publish' | 'measure';

export type SideStepId = 'leadMagnets' | 'flyers' | 'memes';

export interface FlowStep {
  id: FlowStepId;
  /** 1-basierte Nummer, wie sie in Sidebar und Schrittleiste erscheint. */
  index: number;
  path: string;
  label: string;
  shortLabel: string;
  description: string;
  icon: IconName;
  requires: FlowStepId[];
  recommends: FlowStepId[];
  /** Beschreibt den erledigten Zustand, z. B. "2 Strategien gespeichert". */
  doneLabel: (count: number) => string;
  /** Beschriftung des Knopfs, der den Schritt beginnt. */
  cta: string;
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

export const FLOW_STEPS: FlowStep[] = [
  {
    id: 'strategy', index: 1, path: '/strategy', label: 'Strategie & Zielkundenprofil (ICP)', shortLabel: 'Strategie', icon: 'compass',
    description: 'Zielgruppe, Ideal Customer Profile und Pain Points ermitteln. Alles Weitere baut darauf auf.',
    requires: [], recommends: [],
    doneLabel: (n) => plural(n, 'Strategie gespeichert', 'Strategien gespeichert'),
    cta: 'Strategie-Analyse starten',
  },
  {
    id: 'profiles', index: 2, path: '/linkedin-profiles', label: 'Referenzprofile', shortLabel: 'Profile', icon: 'users',
    description: 'LinkedIn-Profile als Stilvorbilder sammeln und ihre besten Beispiel-Posts hinterlegen.',
    requires: [], recommends: ['strategy'],
    doneLabel: (n) => plural(n, 'Profil gespeichert', 'Profile gespeichert'),
    cta: 'Profile anlegen',
  },
  {
    id: 'series', index: 3, path: '/series-formats', label: 'Serienformate', shortLabel: 'Serien', icon: 'layers',
    description: 'Wiederkehrende Beitragsformate festlegen, die zur Strategie passen – gern per KI-Vorschlag.',
    requires: [], recommends: ['strategy'],
    doneLabel: (n) => plural(n, 'Serienformat', 'Serienformate'),
    cta: 'Serienformate festlegen',
  },
  {
    id: 'ideas', index: 4, path: '/post-ideas', label: 'Beitragsideen', shortLabel: 'Ideen', icon: 'lightbulb',
    description: 'Konkrete Beitragsideen je Serienformat sammeln. Die KI-Generierung braucht eine Strategie.',
    requires: ['strategy'], recommends: ['series'],
    doneLabel: (n) => plural(n, 'Beitragsidee', 'Beitragsideen'),
    cta: 'Ideen generieren',
  },
  {
    id: 'briefs', index: 5, path: '/content-briefs', label: 'Beitragsentwürfe (Content Briefs)', shortLabel: 'Entwürfe', icon: 'file-text',
    description: 'Je Beitragsidee ein knappes Brief: Thema, Herausforderung, Wendepunkt, Learning.',
    requires: ['ideas'], recommends: [],
    doneLabel: (n) => plural(n, 'Brief', 'Briefs'),
    cta: 'Briefs schreiben',
  },
  {
    id: 'post', index: 6, path: '/linkedin-post-generator', label: 'Beitrag erstellen', shortLabel: 'Beitrag', icon: 'edit',
    description: 'Aus einem Brief fertige Beiträge mit Hook, Content, CTA und Footer erzeugen – als A/B-Varianten.',
    requires: ['briefs'], recommends: ['profiles'],
    doneLabel: (n) => plural(n, 'Beitrag erstellt', 'Beiträge erstellt'),
    cta: 'Beitrag generieren',
  },
  {
    id: 'plan', index: 7, path: '/editorial-plan', label: 'Wochenplan (Slots)', shortLabel: 'Wochenplan', icon: 'calendar',
    description: 'Wochenplan mit Slots anlegen und fertige Beiträge auf Tage verteilen. Terminiert und freigegeben wird im nächsten Schritt.',
    requires: [], recommends: ['post'],
    doneLabel: (n) => plural(n, 'Wochenplan', 'Wochenpläne'),
    cta: 'Wochenplan anlegen',
  },
  {
    id: 'calendar', index: 8, path: '/publication-calendar', label: 'Terminieren', shortLabel: 'Terminieren', icon: 'clock',
    description: 'Beiträge auf Datum und Uhrzeit legen. Freigabe und Versand laufen unter Freigabe & Versand.',
    requires: ['post'], recommends: [],
    doneLabel: (n) => plural(n, 'Beitrag terminiert', 'Beiträge terminiert'),
    cta: 'Terminieren öffnen',
  },
  {
    id: 'measure', index: 9, path: '/linkedin-statistics', label: 'Veröffentlichen & Messen', shortLabel: 'Messen', icon: 'bar-chart',
    description: 'LinkedIn-Konto verbinden, veröffentlichen und die Kennzahlen für die nächste Runde nutzen.',
    requires: ['calendar'], recommends: [],
    doneLabel: (n) => plural(n, 'Beitrag veröffentlicht', 'Beiträge veröffentlicht'),
    cta: 'Statistiken ansehen',
  },
];

export interface SideStep {
  id: SideStepId;
  path: string;
  label: string;
  description: string;
  icon: IconName;
}

export const SIDE_STEPS: SideStep[] = [
  { id: 'leadMagnets', path: '/lead-magnets', label: 'Lead-Magnete', icon: 'magnet', description: 'Whitepaper, Checklisten oder Guides als Gegenwert für Kontaktdaten.' },
  { id: 'flyers', path: '/flyer-generator', label: 'Flyer', icon: 'image', description: 'Beitragsideen als Bild-Flyer rendern und direkt posten.' },
  { id: 'memes', path: '/meme-generator', label: 'Memes', icon: 'smile', description: 'Auflockernde Varianten für A/B-Tests.' },
];

/** Reiter innerhalb einer Phase, der kein Prozessschritt mit Status ist. */
export interface PhaseExtraTab {
  path: string;
  label: string;
  icon: IconName;
}

export interface FlowPhase {
  id: FlowPhaseId;
  /** 1-basierte Nummer, wie sie in Seitenleiste und Schrittleiste erscheint. */
  index: number;
  label: string;
  description: string;
  icon: IconName;
  /** Schritte der Phase in Reihenfolge; der erste ist die Einstiegsseite. */
  steps: FlowStepId[];
  /** Weitere Reiter ohne Prozessstatus (z. B. Freigabe-Warteschlange). */
  extraTabs: PhaseExtraTab[];
}

/**
 * Der Prozess in fuenf Phasen. Jede Phase ist ein Menueeintrag und eine
 * Seite mit Reitern; die Schritte darin behalten ihre Routen, Daten und
 * ihren Status.
 */
export const FLOW_PHASES: FlowPhase[] = [
  {
    id: 'foundation', index: 1, label: 'Grundlagen', icon: 'compass',
    description: 'Zielgruppe und Stil festlegen: Strategie mit ICP und Pain Points, dazu Referenzprofile als Vorbilder.',
    steps: ['strategy', 'profiles'], extraTabs: [],
  },
  {
    id: 'formats', index: 2, label: 'Formate & Ideen', icon: 'lightbulb',
    description: 'Wiederkehrende Serienformate festlegen und je Format konkrete Beitragsideen sammeln.',
    steps: ['series', 'ideas'], extraTabs: [],
  },
  {
    id: 'posts', index: 3, label: 'Beiträge', icon: 'edit',
    description: 'Aus Ideen knappe Briefs machen und daraus fertige Beiträge mit Hook, Content und CTA erzeugen.',
    steps: ['briefs', 'post'], extraTabs: [{ path: '/media', label: 'Medienbibliothek', icon: 'archive' }],
  },
  {
    id: 'publish', index: 4, label: 'Planen & Veröffentlichen', icon: 'calendar',
    description: 'Fertige Beiträge auf Slots verteilen, terminieren, freigeben und posten.',
    steps: ['plan', 'calendar'],
    extraTabs: [{ path: '/scheduled-posts', label: 'Freigabe & Versand', icon: 'send' }],
  },
  {
    id: 'measure', index: 5, label: 'Messen', icon: 'bar-chart',
    description: 'LinkedIn-Konto verbinden und die Kennzahlen für die nächste Runde nutzen.',
    steps: ['measure'], extraTabs: [],
  },
];

export const FLOW_PHASE_COUNT = FLOW_PHASES.length;

const phaseByIdMap = new Map(FLOW_PHASES.map((p) => [p.id, p]));
const phaseByStep = new Map<FlowStepId, FlowPhase>();
for (const phase of FLOW_PHASES) for (const id of phase.steps) phaseByStep.set(id, phase);

export function flowPhaseById(id: FlowPhaseId): FlowPhase {
  return phaseByIdMap.get(id) as FlowPhase;
}

export function phaseOfStep(id: FlowStepId): FlowPhase {
  return phaseByStep.get(id) as FlowPhase;
}

/** Einstiegspfad einer Phase (ihr erster Schritt). */
export function phasePath(phase: FlowPhase): string {
  return flowStepById(phase.steps[0]).path;
}

export function flowPhaseByPath(pathname: string): FlowPhase | undefined {
  const step = flowStepByPath(pathname);
  if (step) return phaseOfStep(step.id);
  return FLOW_PHASES.find((p) => p.extraTabs.some((t) => t.path === pathname || pathname.startsWith(`${t.path}/`)));
}

export const FLOW_STEP_COUNT = FLOW_STEPS.length;

const byId = new Map(FLOW_STEPS.map((s) => [s.id, s]));

export function flowStepById(id: FlowStepId): FlowStep {
  return byId.get(id) as FlowStep;
}

export function flowStepByPath(pathname: string): FlowStep | undefined {
  return FLOW_STEPS.find((s) => s.path === pathname || pathname.startsWith(`${s.path}/`));
}
